import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import { Heading } from '~/components/core';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import AuditLogTable from '~/components/SystemConfiguration/ConfigurationManagement/AuditLog/AuditLogTable';
import type { UserModel } from '~/components/types/user-types';
import useDataFetcher from '~/components/useDataFetcher';
import type { AuditedConfigurationChangeListAuditedConfigurationChange } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import type { ChangeRequest } from './types';

interface DeployChangeRequestDialogProps {
  onClose: () => void;
  organization: Record<string, unknown>;
  onSuccess: () => void;
  changeRequest: ChangeRequest;
  users?: UserModel[];
}

interface DeployChangeRequestForm {
  deployment_comment: string;
}

const DeployChangeRequestDialog: React.FC<DeployChangeRequestDialogProps> = ({
  onClose,
  organization,
  onSuccess,
  changeRequest,
  users,
}) => {
  const [configurationChanges, setConfigurationChanges] = useState<
    AuditedConfigurationChangeListAuditedConfigurationChange[]
  >([]);

  // Fetch change request details including configuration changes
  const {
    isLoading: isLoadingDetails,
    isError: isErrorDetails,
    data: changeRequestDetails,
  } = useDataFetcher<{
    change_request: ChangeRequest;
    configuration_changes: AuditedConfigurationChangeListAuditedConfigurationChange[];
  }>(`/sysconfig/api/v1/organizations/${organization.id}/change_requests/${changeRequest.changeset_uuid}`);

  useEffect(() => {
    if (changeRequestDetails?.configuration_changes) {
      setConfigurationChanges(changeRequestDetails.configuration_changes);
    }
  }, [changeRequestDetails]);

  const initialValues: DeployChangeRequestForm = {
    deployment_comment: 'deployed',
  };

  const validationSchema = Yup.object();

  const handleSubmit = async (values: DeployChangeRequestForm) => {
    try {
      const requestData = {
        deployment_comment: values.deployment_comment,
      };
      await axios.post(
        `/sysconfig/api/v1/organizations/${organization.id}/change_requests/${changeRequest.changeset_uuid}/deploy`,
        requestData
      );

      onSuccess();
    } catch (error) {
      reportAxiosError(error);
      throw error;
    }
  };

  const displayLoading = isLoadingDetails || isErrorDetails || !changeRequestDetails || !users;

  const getUserFullName = (userId: string) => {
    const user = users?.find((user) => user.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : userId;
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await handleSubmit(values);
        } catch (error) {
          setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <CardDialog
          isDialog
          title="Deploy Change Request"
          maxWidth="xl"
          onClose={onClose}
          preventClose={isSubmitting}
          footerActions={
            <DialogFooterActions
              onClickPrimary={handleSubmit}
              onClickSecondary={onClose}
              primaryLabel="Deploy Change Request"
              showPrimary
              secondaryLabel="Cancel"
              ignorePermissionsSecondary
            />
          }
        >
          <div className="space-y-24">
            {/* Change Request Info */}
            <div className="bg-slate-50 rounded-md border border-slate-200 p-16">
              <div className="grid grid-cols-2 gap-16">
                <div>
                  <div className="text-sm font-medium text-slate-700">Description</div>
                  <div className="text-sm text-slate-900">
                    {changeRequest.changeset_description || 'No description provided'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-700">Requested By</div>
                  <div className="text-sm text-slate-900">{getUserFullName(changeRequest.requesting_user_id)}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-700">Approved By</div>
                  <div className="text-sm text-slate-900">
                    {changeRequest.approving_user_id ? getUserFullName(changeRequest.approving_user_id) : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-700">Changes Count</div>
                  <div className="text-sm text-slate-900">{changeRequest.changes_count}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-700">Created At</div>
                  <div className="text-sm text-slate-900">{new Date(changeRequest.created_at).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-700">Approved At</div>
                  <div className="text-sm text-slate-900">
                    {changeRequest.approved_at ? new Date(changeRequest.approved_at).toLocaleString() : 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Configuration Changes Table */}
            <div>
              <Heading className="mb-16">Configuration Changes</Heading>
              <div className="rounded-md border border-slate-200 p-16">
                {displayLoading ? (
                  <SkeletonTable rowsCount={5} columnsCount={8} isError={isErrorDetails} />
                ) : (
                  <>
                    <div className="mb-16 text-sm text-slate-600">
                      The following configuration changes will be deployed:
                    </div>
                    <AuditLogTable
                      rows={configurationChanges}
                      users={users || []}
                      selectedChangeIds={[]}
                      onSelectedChangeIdsChange={() => {}}
                      withSelectChanges={false}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default DeployChangeRequestDialog;
